/**
 * Adgang til access tokenet for HTTP-klienten.
 *
 * Ligger i sin egen modul-lokale variabel frem for i Zustand-storen, fordi
 * `client.ts` ellers skulle importere auth-featuren, som selv importerer
 * klienten. Storen skriver hertil; klienten læser.
 *
 * Tokenet gemmes bevidst kun i hukommelsen. Ved en genindlæsning hentes et
 * nyt via refresh token-cookien. Lå det i localStorage, kunne et XSS-angreb
 * læse det.
 */

let accessToken: string | null = null

/** Sættes af auth-storen. Bruges til at forny et udløbet access token. */
let refreshHandler: (() => Promise<string | null>) | null = null

/** Kaldes når fornyelse endeligt mislykkes, så appen kan sende brugeren til login. */
let sessionExpiredHandler: (() => void) | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

export function registerRefreshHandler(handler: () => Promise<string | null>): void {
  refreshHandler = handler
}

export function registerSessionExpiredHandler(handler: () => void): void {
  sessionExpiredHandler = handler
}

export function notifySessionExpired(): void {
  accessToken = null
  sessionExpiredHandler?.()
}

/**
 * Fornyer access tokenet — højst én gang ad gangen.
 *
 * Uden denne sammenlægning ville ti samtidige 401'er udløse ti
 * fornyelseskald. Da hver fornyelse roterer refresh tokenet, ville de ni
 * sidste præsentere et allerede forbrugt token og udløse serverens
 * tyverisdetektion, som lukker hele sessionen.
 */
let inFlightRefresh: Promise<string | null> | null = null

export function refreshAccessToken(): Promise<string | null> {
  if (!refreshHandler) {
    return Promise.resolve(null)
  }

  inFlightRefresh ??= refreshHandler().finally(() => {
    inFlightRefresh = null
  })

  return inFlightRefresh
}
